import { push } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import { useGetAccounts } from "hooks/useAccounts";
import useSchema from "hooks/useSchema";
import { isEmpty } from "lodash";
import moment from "moment";
import Multilinefile from "pages/forms/_components/_fields/Multilinefile";
import Action from "pages/_components/Action";
import Button from "pages/_components/Button";
import Dropdown from "pages/_components/Dropdown";
import Date from "pages/_components/fields/DateField";
import FieldLabel from "pages/_components/fields/FieldLabel";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Selector from "pages/_components/fields/formik/Selector";
import { DesktopButtons, MobileButtons, ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { actions as fileActions } from "reducers/files";
import { actions as notificationActions } from "reducers/notification";

import { actions as thirdPaymentActions } from "reducers/thirdPayment";
import { filterForAvailableDays } from "util/date";
import * as i18n from "util/i18n";
import { lotPaymentTypes } from "util/thirdPayment";
import * as Yup from "yup";
import { isIOS } from "react-device-detect";
import * as configUtils from "util/config";
import Loader from "pages/_components/Loader";
import CustomData from "pages/_components/CustomData/CustomData";
import I18n from "pages/_components/I18n";
import Image from "pages/_components/Image";

const FORM_ID = "payments.newPayment";

const NewLotPaymentPage = () => {
    const { multilineFile: processedFileData } = useSelector((state) => state.formFields);
    const { fetching: fetchingDownload } = useSelector((state) => state.files);
    const { thirdPaymentData, fetching: isProccessingFile, isValidDate } = useSelector((state) => state.thirdPayment);
    const lotPaymentOptions = i18n.formatOption(lotPaymentTypes, "lotPayment.type");
    const { stringRequired } = useSchema();
    const { accountOptions, fetching, accounts, dispatch } = useGetAccounts("", "", "", true, true);
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [resetDatePicker, setResetDatePicker] = useState(false);
    const [fetchingDateValidation, setFetchingDateValidation] = useState(false);
    const regexFileNameFormat = {
        PP: /(TMPAGOPROV)_\d{4}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01]).(xls|xlsx)/g,
        PH: /(TMPAGOHAB)_\d{4}(0[1-9]|1[012])(0[1-9]|[12][0-9]|3[01]).(xls|xlsx)/g,
    };
    const [form, setForm] = useState(null);
    const [hasEnoughBalance, setHasEnoughBalance] = useState(false);
    const [trxLimitExceeded, setTrxLimitExceeded] = useState(false);
    const [canContinueWithTx, setCanContinueWithTx] = useState(false);

    const initialValues = {
        debitAccount: thirdPaymentData?.debitAccount || "",
        file: null,
        typePayment: thirdPaymentData?.typePayment || "",
        dateProcess: thirdPaymentData?.dateProcess || null,
    };

    const STARTING_HOUR = configUtils.getInteger("bibank.transfer.closingHourStart", 16);
    const MAX_TRANSACTIONS_PER_BATCH = configUtils.getInteger("lotPayments.maxTransactionsPerBatch");

    const handleSubmit = (values) => {
        const completeDebitAccount = accounts.find((account) => account.productId === values.debitAccount);
        const _thirdPaymentData = {
            ...values,
            accountInfo: {
                accountNumber: completeDebitAccount.accountNumber,
                accountType: completeDebitAccount.accountType,
                currency: completeDebitAccount.currency,
                shortLabel: completeDebitAccount.shortLabel,
            },
            processedFileData,
            amount: processedFileData.totalAmount,
            idForm: "transfersThirdParties",
        };
        dispatch(thirdPaymentActions.summaryThirdPay(_thirdPaymentData));
        dispatch(push(`/lotPayments/newPayment/confirm`));
    };

    useEffect(() => {
        const isToday = moment(form?.dateProcess).isSame(moment(), "day");
        const defaultTZ = configUtils.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");
        const end = configUtils.get("transfer.schedule.endTime", "16:00");
        const maxDateHour = moment()
            .tz(defaultTZ)
            .set({
                hour: end.split(":")[0],
                minute: end.split(":")[1],
                second: 0,
                millisecond: 0,
            });

        const selected = moment(form?.dateProcess).tz(defaultTZ);

        if (processedFileData?.hasFile) {
            if (isToday && !selected.isAfter(maxDateHour)) {
                const selectedAccount = accountOptions.filter((account) => account.id === form.debitAccount);
                const notEnoughBalance = selectedAccount[0]?.balance.quantity < processedFileData.totalAmount.quantity;
                if (notEnoughBalance) {
                    setHasEnoughBalance(false);
                    dispatch(
                        notificationActions.showNotification(
                            i18n.get("transfer.thirdParties.debitAccount.insufficientBalance"),
                            "error",
                            ["newLotPaymentPage"],
                        ),
                    );
                } else {
                    setHasEnoughBalance(true);
                }
            } else {
                setHasEnoughBalance(true);
            }
        }
    }, [processedFileData?.hasFile, form?.dateProcess]);

    useEffect(() => {
        if (processedFileData?.hasFile) {
            // Verifico que no haya pasado el limite de transacciones por archivo
            setTrxLimitExceeded(processedFileData?.validLines > MAX_TRANSACTIONS_PER_BATCH);
            if (trxLimitExceeded) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("transfer.massive.maxTransactionsPerBatch.snackbar", null, {
                            maxTransactionsPerBatch: MAX_TRANSACTIONS_PER_BATCH,
                        }),
                        "error",
                        ["newLotPaymentPage"],
                    ),
                );
                setTrxLimitExceeded(false);
            }
            setCanContinueWithTx(trxLimitExceeded);
        }
    }, [processedFileData]);

    const lotPaymentsValidationSchema = Yup.lazy((values) =>
        Yup.object().shape({
            debitAccount: stringRequired,
            file: values.file
                ? Yup.array()
                      .of(Yup.object())
                      .min(1, i18n.get(`global.error.required`))
                : Yup.array().notRequired(),
            typePayment: stringRequired,
            dateProcess: Yup.date()
                .nullable()
                .required(i18n.get(`global.error.required`)),
        }),
    );

    const getMinDate = () => {
        const holidays = [];
        const date = moment();
        while (!filterForAvailableDays(holidays, date)) {
            date.add(-1, "days");
        }

        setMinDate(date);
    };
    useEffect(() => {
        const maxDays = configUtils.getInteger("time.frecuency.max.days", 365);
        setMaxDate(moment().add(maxDays, "days"));
    }, []);

    useEffect(() => {
        getMinDate();
    }, []);

    useEffect(() => {
        if (fetchingDateValidation && isValidDate !== null) {
            if (!isValidDate) {
                setResetDatePicker(true);
            }
            setFetchingDateValidation(false);
        }
    }, [fetchingDateValidation, isValidDate]);

    const handleChangeDate = (selectedDate) => {
        const isToday = moment(selectedDate).isSame(moment(), "day");

        const defaultTZ = configUtils.get("administration.timezone.defaultValue", "America/Argentina/Buenos_Aires");
        const end = configUtils.get("transfer.schedule.endTime", "16:00");
        const maxDateHour = moment()
            .tz(defaultTZ)
            .set({
                hour: end.split(":")[0],
                minute: end.split(":")[1],
                second: 0,
                millisecond: 0,
            });

        const selected = moment(selectedDate).tz(defaultTZ);

        if (isToday && selected.isAfter(maxDateHour)) {
            const start = configUtils.get("transfer.schedule.time", "08:00");
            dispatch(
                notificationActions.showNotification(
                    i18n.get("lotPayments.max.hour.msg", "", { START_TIME: Number(start.split(":")[0]) }),
                    "warning",
                    ["newLotPaymentPage"],
                ),
            );
            // setResetDatePicker(true);
        }
        const formattedDate = moment(selectedDate)?.format("YYYY-MM-DD");
        dispatch(thirdPaymentActions.validateDate(formattedDate));
        setFetchingDateValidation(true);
    };

    const handleDownload = (formatDownload) => {
        if (formatDownload.includes("hab")) {
            dispatch(
                fileActions.downloadTemplate(
                    "TEMPLATE_TRANSFER_HEBERES",
                    "TMPAGOHAB_AAAAMMDD",
                    "txt",
                    "newLotPaymentPage",
                ),
            );
            return;
        }
        dispatch(
            fileActions.downloadTemplate(
                "TEMPLATE_TRANSFER_PROVEEDORES",
                "TMPAGOPROV_AAAAMMDD",
                "txt",
                "newLotPaymentPage",
            ),
        );
    };

    return (
        <>
            <Notification scopeToShow="newLotPaymentPage" />
            <ResponsiveHeader id="payments.newLotPayments.title">
                <DesktopButtons>
                    <Dropdown
                        image="images/figma/icon/download.svg"
                        label="lotPayment.template.download"
                        buttonClass="btn btn-outline"
                        fetching={fetchingDownload}
                        pullRight
                        widthXl>
                        <Button
                            onClick={() => handleDownload("pro")}
                            label="lotPayment.template.pro"
                            className="dropdown__item-btn"
                            bsStyle="link"
                        />
                        <Button
                            onClick={() => handleDownload("hab")}
                            label="lotPayment.template.hab"
                            className="dropdown__item-btn"
                            bsStyle="link"
                        />
                    </Dropdown>
                </DesktopButtons>
                <MobileButtons
                    image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}>
                    <Action labelKey="lotPayment.template.pro" handleClick={() => handleDownload("pro")} />
                    <Action labelKey="lotPayment.template.hab" handleClick={() => handleDownload("hab")} />
                </MobileButtons>
            </ResponsiveHeader>

            <MainContainer showLoader={fetching}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={lotPaymentsValidationSchema}>
                    {({ values }) => {
                        const showFileUploader = values.debitAccount && values.typePayment && values.dateProcess;
                        setForm(values);
                        const canSubmit =
                            !values.debitAccount ||
                            !values.typePayment ||
                            !values.dateProcess ||
                            isEmpty(values.file) ||
                            !hasEnoughBalance ||
                            canContinueWithTx;
                        return (
                            <Form>
                                <Row className="form-background new-thirdparty-payments-table">
                                    <Col xs={12} md={5} mdOffset={1}>
                                        <Field
                                            component={Selector}
                                            label={i18n.get(`${FORM_ID}.typePayment.label`, "")}
                                            name="typePayment"
                                            placeholder={i18n.get("global.select.option.placeholder")}
                                            options={lotPaymentOptions}
                                            disabled={!isEmpty(values.file)}
                                        />
                                    </Col>
                                    <Col xs={12} md={5} mdOffset={6}>
                                        <Field
                                            component={ProductSelector}
                                            label={i18n.get(`${FORM_ID}.debitAccountId.label`, "")}
                                            name="debitAccount"
                                            placeholder={i18n.get("global.select.account.placeholder")}
                                            data={{ emptyOptionLabel: "", options: accountOptions }}
                                            disabled={!isEmpty(values.file)}
                                        />
                                    </Col>

                                    <Col xs={12} md={5} mdOffset={1} className="required">
                                        <FieldLabel
                                            labelText={i18n.get(`${FORM_ID}.dateProcess.label`, "")}
                                            idField="dateProcess"
                                        />
                                        {fetchingDateValidation ? (
                                            <Loader />
                                        ) : (
                                            <Field
                                                component={Date}
                                                idForm={FORM_ID}
                                                name="dateProcess"
                                                startDate={minDate}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                filterDate={(date) => filterForAvailableDays([], date)}
                                                handleChange={handleChangeDate}
                                                withTodayValidation
                                                resetFilters={resetDatePicker}
                                                handleResetDateFilters={setResetDatePicker}
                                                hideLabel
                                                noEdit
                                            />
                                        )}
                                    </Col>
                                    {showFileUploader && (
                                        <Col xs={10} mdOffset={1}>
                                            <FieldLabel labelKey={`${FORM_ID}.file.label`} />
                                            <Field
                                                name="file"
                                                component={Multilinefile}
                                                type="multilinefile"
                                                mode="edit"
                                                formVersion="1"
                                                sub_type="transfersThirdParties"
                                                formTitle="transfersThirdParties"
                                                allowMultiple="false"
                                                acceptedFileTypes={[
                                                    "application/vnd.ms-excel",
                                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                                ]}
                                                useForTotalAmount="true"
                                                maxFileSizeMB={4}
                                                idField="file"
                                                idForm="transfersThirdParties"
                                                idActivity="transfers.third.parties"
                                                action={thirdPaymentActions.downloadTransactionLines}
                                                notificationScope="newLotPaymentPage"
                                                successNotificationMessage="forms.notification.uploadFile.success"
                                                warningNotificationMessage="forms.inputFile.massivePayments.warning"
                                                fileErrorMessage="forms.inputFile.massivePayments.invalid.file.type"
                                                showErrorLabel={false}
                                                linesInfoMessage="forms.inputFile.massivePayments.success"
                                                errorNotificationMessage=""
                                                description={values.debitAccount}
                                                fileNameRegex={regexFileNameFormat[values.typePayment]}
                                                isRequired
                                                hasEnoughBalance={hasEnoughBalance}
                                                trxLimitExceeded={trxLimitExceeded}
                                                setTrxLimitExceeded={setTrxLimitExceeded}
                                            />
                                        </Col>
                                    )}
                                </Row>

                                <Row>
                                    <div className="container--alert" role="alert">
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18n.get("transfer.massive.disclaimer", null, {
                                                    STARTING_HOUR,
                                                })}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                        <div className="alert alert-info">
                                            <div className="custom-data  with-icon">
                                                <div>
                                                    <Image src="information_fill.svg" />
                                                </div>
                                                <div>
                                                    <I18n id="transfer.massive.maxTransactionsPerBatchReminder" />
                                                    {i18n.get("transfer.massive.maxTransactionsPerBatch", null, {
                                                        maxTransactionsPerBatch: MAX_TRANSACTIONS_PER_BATCH,
                                                    })}
                                                    <I18n id="transfer.massive.maxTransactionsPerBatchPart2" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="alert alert-info">
                                            <CustomData
                                                icon="information_fill.svg"
                                                titleText={i18n.get("transfer.massive.reminder3")}
                                                keepSpaceIfNoValue={false}
                                            />
                                        </div>
                                    </div>
                                </Row>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            disabled={canSubmit}
                                            loading={isProccessingFile}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

export default NewLotPaymentPage;
